import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './App.css'
import TrainingContent from './TrainingContent'
import LanguageSwitcher from './LanguageSwitcher'
import RoleSelector from './components/RoleSelector'
import OfficerPhaseProgression from './components/OfficerPhaseProgression'
import CertificationTracker from './components/CertificationTracker'
import ManagerDashboard from './components/ManagerDashboard'
import TeamMemberDashboard from './components/TeamMemberDashboard'

export default function App() {
  const { t } = useTranslation()
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [isManager, setIsManager] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const validateSession = async () => {
      try {
        const response = await fetch('/api/training/session', { credentials: 'include' })
        if (!response.ok) {
          window.location.href = '/training/login'
          return
        }
        const session = await response.json()
        localStorage.setItem('user_id', session.user_id)

        const userRes = await fetch(`/api/training/user-info?userId=${encodeURIComponent(session.user_id)}`, { credentials: 'include' })
        if (!userRes.ok) {
          window.location.href = '/training/login'
          return
        }
        const userInfo = await userRes.json()
        setUser(userInfo)
        setIsManager(!!userInfo.canManageTeams)

        // Restore the track the user picked last time
        try {
          const profRes = await fetch(`/api/training/user-profile`, { credentials: 'include' })
          if (profRes.ok) {
            const profile = await profRes.json()
            if (profile.primary_role) setUserRole(profile.primary_role)
            if (profile.certificate_name) localStorage.setItem(`training_name_${userInfo.id}`, profile.certificate_name)
          }
        } catch (err) {
          console.warn('[App] Could not load training profile:', err)
        }
        setIsLoading(false)
      } catch (err) {
        console.error('[App] Session validation error:', err)
        window.location.href = '/training/login'
      }
    }

    validateSession()
  }, [])

  const handleRoleSelected = (role) => {
    setUserRole(role)
  }

  const handleLogout = async () => {
    try {
      await fetch('/api/training/logout', { method: 'POST', credentials: 'include' })
    } catch { /* ignore */ }
    localStorage.removeItem('training_session')
    localStorage.removeItem('user_id')
    window.location.href = '/training/login'
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    )
  }

  // Show role selector if user hasn't selected a role yet
  if (!userRole) {
    return (
      <>
        <RoleSelector onRoleSelected={handleRoleSelected} userEmail={user?.email} userId={user?.id} />
        <footer className="training-footer">
          <p>&copy; {new Date().getFullYear()} CrossReg Nexus Consultants. All rights reserved.</p>
          <a href="/crossregnexus.html">{t('manager.backToHome')}</a>
        </footer>
      </>
    )
  }

  return (
    <div className="training-app">
      {/* Header */}
      <header className="training-header">
        <div className="header-content">
          <div>
            <h1>CrossReg Training</h1>
          </div>
          <div className="header-right">
            <LanguageSwitcher />
            <div className="user-info">
              <div className="user-name">{user?.firstName} {user?.lastName}</div>
              <div className="user-email">{user?.email}</div>
              <button
                onClick={() => setUserRole(null)}
                className="role-switch-btn"
                title="Switch training track"
              >
                {t('common.switchTrack')}
              </button>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              {t('common.signOut')}
            </button>
            <button
              onClick={() => (window.location.href = '/crossregnexus.html')}
              className="home-btn"
            >
              {t('common.backToHome')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - Show Role-Specific Component */}
      <main className="training-main">
        {userRole === 'compliance-team' && isManager && (
          <ManagerDashboard userId={user?.id} userEmail={user?.email} />
        )}

        {userRole === 'compliance-team' && !isManager && (
          <TeamMemberDashboard userId={user?.id} />
        )}

        {userRole === 'compliance-officer' && (
          <OfficerPhaseProgression userId={user?.id} />
        )}

        {userRole === 'individual' && (
          <CertificationTracker userId={user?.id} userEmail={user?.email} />
        )}

        {userRole === 'certification' && (
          <CertificationTracker userId={user?.id} userEmail={user?.email} />
        )}

        {userRole === 'compliance-program' && (
          <TrainingContent userId={user?.id} userEmail={user?.email} />
        )}
      </main>

      {/* Footer */}
      <footer className="training-footer">
        <p>&copy; {new Date().getFullYear()} CrossReg Nexus Consultants. All rights reserved.</p>
        <a href="/crossregnexus.html">{t('common.backToHome')}</a>
      </footer>
    </div>
  )
}
